import React from 'react';
import { Redirect } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { Campaign, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

import { CampaignPageLayout } from '../components/CampaignPageLayout';
import { AdCampaignDetails } from '../components/forms/AdCampaignDetails/AdCampaignDetails';
import { PromoCampaignDetails } from '../components/forms/PromoCampaignDetails/PromoCampaignDetails';
import { useCampaignModuleContext } from '../context';
import { useAdCampaign } from '../hooks/useAdCampaign';
import { useCampaign } from '../hooks/useCampaign';
import useCampaignsOptions from '../hooks/useCampaignsOptions';
import { useCampaignsRoutes } from '../hooks/useCampaignsRoutes';
import { AdCampaign, CampaignAction, CampaignsRoutesParams } from '../types';
import { getCampaignActions } from '../utils/getCampaignActions';
import { getCampaignStatus } from '../utils/getCampaignStatus';
import { useTelemetryCampaignDetailsView } from '../utils/telemetry/telemetry';

export type DetailsCampaignPageProps = {
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
  menuId?: string;
  campaignTypesPermittedToEdit: CampaignType[];
};

export const DetailsCampaignPage = ({
  campaignTypesPermittedToEdit,
  venueDetails,
  menuId,
}: DetailsCampaignPageProps) => {
  const {
    params: { campaignId },
  } = useRouteMatch<CampaignsRoutesParams>();
  const { entityType, country, venues, merchantId } = useCampaignModuleContext();
  const venueIds = venues.map(venue => venue.id);

  const campaignsRoutes = useCampaignsRoutes();
  const { getCampaignTitleByType } = useCampaignsOptions();
  const [queryParams] = useQueryParams({
    campaignType: StringParam,
  });

  const isAdCampaign = queryParams.campaignType === CampaignType.ADS;
  const isAdCampaignsEditable = campaignTypesPermittedToEdit.includes(CampaignType.ADS);

  const { campaign, isCampaignLoading } = useCampaign({
    merchantId,
    campaignId,
    enabled: !isAdCampaign,
  });
  const { adCampaign, isAdCampaignLoading } = useAdCampaign({
    venueIds,
    campaignId,
    enabled: isAdCampaign && isAdCampaignsEditable,
  });

  const isDataLoading = isAdCampaign ? isAdCampaignLoading : isCampaignLoading;
  const campaignStatus = isAdCampaign ? adCampaign?.status : getCampaignStatus(campaign); // TODO: @adtech revert back after proper transormation
  const campaignVenueCount = isAdCampaign ? 1 : campaign?.venue_ids.length; // Ad Campaign only support single venue id.

  const currentCampaign: AdCampaign | Campaign | undefined = campaign || adCampaign;

  useTelemetryCampaignDetailsView(currentCampaign?.campaign_type);

  const availableActions: CampaignAction[] | undefined =
    campaignStatus &&
    currentCampaign &&
    getCampaignActions({
      campaignType: currentCampaign.campaign_type,
      campaignStatus,
      country,
      isCreatedByWolt: 'created_by_wolt' in currentCampaign ? currentCampaign.created_by_wolt : undefined,
      isMerchantLevel: entityType === 'merchant',
      venueIds: 'venue_ids' in currentCampaign ? currentCampaign.venue_ids : venueIds,
    });

  if ((adCampaign && !isAdCampaignsEditable) || (campaign && !availableActions?.includes('read'))) {
    return <Redirect to={campaignsRoutes.index} />;
  }

  return (
    <CampaignPageLayout
      heading={getCampaignTitleByType(currentCampaign?.campaign_type)}
      isLoading={isDataLoading}
      status={campaignStatus}
      venueCount={campaignVenueCount}
    >
      {campaign && (
        <PromoCampaignDetails
          campaignTypesPermittedToEdit={campaignTypesPermittedToEdit}
          campaign={campaign}
          venueDetails={venueDetails}
          menuId={menuId}
        />
      )}
      {adCampaign && <AdCampaignDetails venueId={venueIds[0]} campaign={adCampaign} venueDetails={venueDetails} />}
    </CampaignPageLayout>
  );
};
